// src/CatalogueManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { validerProduit, getChampsTechniques, LIBELLES_CHAMPS, TYPE_CHAMPS, produitIncomplet } from './catalogueValidation';
import { Search, Save, Plus, Trash2, Star, Eye, EyeOff, ChevronDown, ChevronUp, Settings, X } from 'lucide-react';

// Categories chargées dynamiquement depuis Supabase

const fmt = (n) => {
  if (!n && n !== 0) return '';
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
};

const CatalogueManager = () => {
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categorieActive, setCategorieActive] = useState('Tous');
  const [editingId, setEditingId] = useState(null);
  const [fullEditId, setFullEditId] = useState(null);
  const [fullEditValues, setFullEditValues] = useState({});
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [nouvelleCategorie, setNouvelleCategorie] = useState('');
  const [showNouvelleCategorie, setShowNouvelleCategorie] = useState(false);
  const [categoriesExtra, setCategoriesExtra] = useState([]);
  const [selected, setSelected] = useState([]);
  const [moveTarget, setMoveTarget] = useState('');
  const [editCategorie, setEditCategorie] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(null); // id du produit en cours d'upload
  const [uploadError, setUploadError] = useState(null);
  const [newProduit, setNewProduit] = useState({
    categorie: 'Panneaux', ref: '', nom: '', prix: '', actif: true, vedette: false, ordre: 0,
    hmt_max: '', debit_max: '', puissance_w: '', puissance_btu: '',
    capacite_ah: '', tension: '', capacite_l: '',
    prix_accessoires: '', prix_installation: ''
  });

  const fetchProduits = useCallback(async () => {
    const { data } = await supabase.from('catalogue_produits').select('*').order('categorie').order('ordre');
    if (data) setProduits(data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchProduits(); }, [fetchProduits]);

  const produitsFiltres = produits.filter(p => {
    const matchCat = categorieActive === 'Tous' || p.categorie === categorieActive;
    const matchSearch = !search || 
      p.ref?.toLowerCase().includes(search.toLowerCase()) ||
      p.nom?.toLowerCase().includes(search.toLowerCase()) ||
      p.categorie?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const startFullEdit = (p) => {
    setFullEditId(p.id);
    setFullEditValues({
      ref: p.ref || '', nom: p.nom || '', categorie: p.categorie || '',
      prix: p.prix ?? '', ordre: p.ordre ?? 0,
      actif: p.actif !== false, vedette: !!p.vedette,
      hmt_max: p.hmt_max ?? '', debit_max: p.debit_max ?? '',
      puissance_w: p.puissance_w ?? '', puissance_btu: p.puissance_btu ?? '',
      capacite_ah: p.capacite_ah ?? '', tension: p.tension ?? '',
      capacite_l: p.capacite_l ?? '',
      prix_accessoires: p.prix_accessoires ?? '',
      prix_installation: p.prix_installation ?? '',
    });
  };

  const saveFullEdit = async () => {
    const validation = validerProduit(fullEditValues);
    if (!validation.valide) {
      alert('Champs requis manquants : ' + validation.manquants.map(m => LIBELLES_CHAMPS[m] || m).join(', '));
      return;
    }
    const payload = { ...fullEditValues };
    const numericFields = ['prix', 'ordre', 'hmt_max', 'debit_max', 'puissance_w', 'puissance_btu',
      'capacite_ah', 'tension', 'capacite_l', 'prix_accessoires', 'prix_installation'];
    numericFields.forEach(f => {
      if (payload[f] === '' || payload[f] === null || payload[f] === undefined) {
        payload[f] = null;
      } else {
        payload[f] = parseFloat(payload[f]) || 0;
      }
    });
    const { error } = await supabase.from('catalogue_produits').update(payload).eq('id', fullEditId);
    if (error) {
      alert("Erreur lors de la sauvegarde : " + error.message);
      return;
    }
    setFullEditId(null);
    setFullEditValues({});
    fetchProduits();
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditValues({
      prix: p.prix || 0,
      prix_accessoires: p.prix_accessoires || '',
      prix_installation: p.prix_installation || '',
      nom: p.nom || '',
    });
  };

  const saveEdit = async (id) => {
    setSaving(true);
    const p = produits.find(x => x.id === id);
    const updates = {
      prix: parseInt(editValues.prix) || 0,
      nom: editValues.nom,
    };
    if (editValues.prix_accessoires !== '') updates.prix_accessoires = parseInt(editValues.prix_accessoires);
    if (editValues.prix_installation !== '') updates.prix_installation = parseInt(editValues.prix_installation);

    await supabase.from('catalogue_produits').update(updates).eq('id', id);
    setSavedId(id);
    setTimeout(() => setSavedId(null), 2000);
    setSaving(false);
    setEditingId(null);
    fetchProduits();
  };

  const toggleActif = async (p) => {
    await supabase.from('catalogue_produits').update({ actif: !p.actif }).eq('id', p.id);
    fetchProduits();
  };

  const toggleVedette = async (p) => {
    await supabase.from('catalogue_produits').update({ vedette: !p.vedette }).eq('id', p.id);
    fetchProduits();
  };

  const deleteProduit = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    const p = produits.find(x => x.id === id);
    await supabase.from('catalogue_produits').delete().eq('id', id);
    fetchProduits();
  };

  const addProduit = async () => {
    const validation = validerProduit(newProduit);
    if (!validation.valide) {
      alert('Champs requis manquants : ' + validation.manquants.map(m => LIBELLES_CHAMPS[m] || m).join(', '));
      return;
    }
    const payload = { ...newProduit };
    const numericFields = ['prix', 'ordre', 'hmt_max', 'debit_max', 'puissance_w', 'puissance_btu',
      'capacite_ah', 'tension', 'capacite_l', 'prix_accessoires', 'prix_installation'];
    numericFields.forEach(f => {
      if (payload[f] === '' || payload[f] === null || payload[f] === undefined) {
        delete payload[f];
      } else {
        payload[f] = parseFloat(payload[f]) || 0;
      }
    });
    const { error } = await supabase.from('catalogue_produits').insert(payload);
    if (error) {
      alert("Erreur lors de l'ajout : " + error.message);
      return;
    }
    setNewProduit({
      categorie: 'Panneaux', ref: '', nom: '', prix: '', actif: true, vedette: false, ordre: 0,
      hmt_max: '', debit_max: '', puissance_w: '', puissance_btu: '',
      capacite_ah: '', tension: '', capacite_l: '',
      prix_accessoires: '', prix_installation: '',
    });
    setShowAddForm(false);
    fetchProduits();
  };

  // ── Traitement image canvas (resize + fond blanc transparent) ────────────
    const processImage = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const MAX = 400;
      let w = img.width, h = img.height;
      if (w > MAX || h > MAX) {
        if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
        else { w = Math.round(w * MAX / h); h = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      // Pas de fond — conserver la transparence PNG
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(blob => { URL.revokeObjectURL(url); resolve(blob); }, 'image/png', 0.95);
    };
    img.onerror = reject;
    img.src = url;
  });

  const handlePhotoUpload = async (produit, file) => {
    setUploadingPhoto(produit.id);
    setUploadError(null);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const blob = await processImage(file);
      const fileName = produit.ref.replace(/[^a-zA-Z0-9-_]/g, '_') + '.png';

      const formData = new FormData();
      formData.append('image', blob, fileName);

      const response = await fetch('https://devis.takoussane.com/api/upload', {
        method: 'POST',
        headers: {
          'x-api-key': 'taka-upload-2026-secret'
        },
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.url) throw new Error("Le serveur n'a pas renvoyé d'URL valide");

      const imageUrlWithCacheBuster = `${data.url}?v=${Date.now()}`;

      const { error: dbErr } = await supabase
        .from('catalogue_produits')
        .update({ image_url: imageUrlWithCacheBuster })
        .eq('id', produit.id);

      if (dbErr) throw dbErr;

      await fetchProduits();
    } catch (e) {
      clearTimeout(timeoutId);
      if (e.name === 'AbortError') {
        setUploadError("Erreur upload : Le serveur VPS a mis trop de temps à répondre (Timeout 10s).");
      } else {
        setUploadError('Erreur upload : ' + e.message);
      }
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handlePhotoDelete = async (produit) => {
    if (!window.confirm('Supprimer la photo de ' + produit.nom + ' ?')) return;
    
    const fileName = produit.ref.replace(/[^a-zA-Z0-9-_]/g, '_') + '.png';
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      await fetch(`https://devis.takoussane.com/api/image/${fileName}`, {
        method: 'DELETE',
        headers: {
          'x-api-key': 'taka-upload-2026-secret'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const { error: dbErr } = await supabase
        .from('catalogue_produits')
        .update({ image_url: null })
        .eq('id', produit.id);

      if (dbErr) throw dbErr;

      await fetchProduits();
    } catch (e) {
      clearTimeout(timeoutId);
      console.error('Erreur lors de la suppression de la photo:', e.message);
      await supabase.from('catalogue_produits').update({ image_url: null }).eq('id', produit.id);
      await fetchProduits();
    }
  };

  const categoriesPresentes = ['Tous', ...Array.from(new Set([...produits.map(p => p.categorie), ...categoriesExtra])).sort()];

  const handleMoveSelected = async () => {
    if (!moveTarget || selected.length === 0) return;
    const updates = selected.map(id => {
      const p = produits.find(x => x.id === id);
      if (!p) return null;
      return supabase.from('catalogue_produits').update({ categorie: moveTarget }).eq('id', p.id);
    }).filter(Boolean);
    await Promise.all(updates);
    setSelected([]);
    setMoveTarget('');
    fetchProduits();
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleCategorieProduit = async (p, newCat) => {
    await supabase.from('catalogue_produits').update({ categorie: newCat }).eq('id', p.id);
    setEditCategorie({});
    fetchProduits();
  };

  if (loading) return <div style={{ padding: '20px', color: '#6b7280', fontSize: '13px' }}>Chargement du catalogue...</div>;

  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif' }}>

      {/* Barre de recherche + bouton ajouter */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher ref, nom, categorie..."
            style={{ width: '100%', boxSizing: 'border-box', paddingLeft: '32px', paddingRight: '12px',
              paddingTop: '8px', paddingBottom: '8px', border: '1px solid #e5e7eb',
              borderRadius: '8px', fontSize: '13px', outline: 'none' }}
          />
        </div>
        <button onClick={() => setShowAddForm(!showAddForm)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
            background: '#1e40af', color: 'white', border: 'none', borderRadius: '8px',
            fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <Plus size={14} /> Ajouter
          {showAddForm ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {uploadError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px',
          padding: '8px 14px', marginBottom: '12px', fontSize: '12px', color: '#dc2626',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {uploadError}
          <button onClick={() => setUploadError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '14px' }}>✕</button>
        </div>
      )}

      {/* Formulaire ajout */}
      {showAddForm && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px',
          padding: '16px', marginBottom: '16px' }}>
          <div style={{ fontWeight: 700, fontSize: '13px', color: '#0369a1', marginBottom: '12px' }}>
            Nouveau produit
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Catégorie</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <select value={showNouvelleCategorie ? '__new__' : newProduit.categorie}
                  onChange={e => {
                    if (e.target.value === '__new__') { setShowNouvelleCategorie(true); }
                    else { setShowNouvelleCategorie(false); setNewProduit({...newProduit, categorie: e.target.value}); }
                  }}
                  style={{ flex: 1, padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }}>
                  {['Tous', ...Array.from(new Set([...produits.map(p => p.categorie), ...categoriesExtra])).sort()].filter(c => c !== 'Tous').map(c => <option key={c} value={c}>{c}</option>)}
                  <option value="__new__">+ Nouvelle catégorie...</option>
                </select>
              </div>
              {showNouvelleCategorie && (<>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                  <input value={nouvelleCategorie} onChange={e => setNouvelleCategorie(e.target.value)}
                    placeholder="Nom de la nouvelle catégorie"
                    style={{ flex: 1, padding: '6px 10px', border: '1px solid #bae6fd', borderRadius: '6px', fontSize: '12px' }} />
                  <button onClick={() => {
                    if (nouvelleCategorie.trim()) {
                      setNewProduit({...newProduit, categorie: nouvelleCategorie.trim()}); setCategoriesExtra(prev => Array.from(new Set([...prev, nouvelleCategorie.trim()])));
                      setShowNouvelleCategorie(false);
                      setNouvelleCategorie('');
                    }
                  }} style={{ padding: '6px 12px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                    OK
                  </button>
                </div>
                  <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>⚠️ Ajoutez un produit dès maintenant, sinon la catégorie disparaîtra.</p>
                </>
              )}
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Référence</label>
              <input value={newProduit.ref} onChange={e => setNewProduit({...newProduit, ref: e.target.value})}
                placeholder="ex: JKM545M"
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Nom</label>
              <input value={newProduit.nom} onChange={e => setNewProduit({...newProduit, nom: e.target.value})}
                placeholder="ex: Panneau Jinko 545Wc"
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Prix (FCFA)</label>
              <input type="number" value={newProduit.prix} onChange={e => setNewProduit({...newProduit, prix: e.target.value})}
                placeholder="ex: 152220"
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px' }}>Ordre</label>
              <input type="number" value={newProduit.ordre} onChange={e => setNewProduit({...newProduit, ordre: parseInt(e.target.value)})}
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                <input type="checkbox" checked={newProduit.vedette} onChange={e => setNewProduit({...newProduit, vedette: e.target.checked})} />
                Vedette
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                <input type="checkbox" checked={newProduit.actif} onChange={e => setNewProduit({...newProduit, actif: e.target.checked})} />
                Actif
              </label>
            </div>
          </div>
          {getChampsTechniques(newProduit.categorie).length > 0 && (
            <div style={{ marginTop: '12px', padding: '12px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px' }}>
              <div style={{ fontWeight: 700, fontSize: '12px', color: '#92400e', marginBottom: '10px' }}>
                Caracteristiques techniques requises pour cette categorie
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                {getChampsTechniques(newProduit.categorie).map(champ => (
                  <div key={champ}>
                    <label style={{ fontSize: '11px', color: '#92400e', display: 'block', marginBottom: '3px', fontWeight: 600 }}>
                      {LIBELLES_CHAMPS[champ] || champ} <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <input
                      type={TYPE_CHAMPS[champ] || 'text'}
                      step="0.01"
                      value={newProduit[champ] || ''}
                      onChange={e => setNewProduit({...newProduit, [champ]: e.target.value})}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '12px' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={addProduit}
              disabled={!validerProduit(newProduit).valide}
              title={!validerProduit(newProduit).valide ? 'Champs requis manquants : ' + validerProduit(newProduit).manquants.map(m => LIBELLES_CHAMPS[m] || m).join(', ') : 'Enregistrer le produit'}
              style={{ padding: '8px 20px', background: validerProduit(newProduit).valide ? '#1e40af' : '#9ca3af', color: 'white', border: 'none',
                borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: validerProduit(newProduit).valide ? 'pointer' : 'not-allowed', opacity: validerProduit(newProduit).valide ? 1 : 0.6 }}>
              Enregistrer
            </button>
            <button onClick={() => setShowAddForm(false)}
              style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: 'none',
                borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Onglets catégories */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
        {categoriesPresentes.map(cat => (
          <button key={cat} onClick={() => setCategorieActive(cat)}
            style={{ padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
              cursor: 'pointer', border: 'none', transition: 'all 0.15s',
              background: categorieActive === cat ? '#1e40af' : '#f3f4f6',
              color: categorieActive === cat ? 'white' : '#6b7280' }}>
            {cat}
            {cat !== 'Tous' && (
              <span style={{ marginLeft: '5px', fontSize: '10px', opacity: 0.8 }}>
                ({produits.filter(p => p.categorie === cat).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Barre déplacement en masse */}
      {selected.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#eff6ff',
          border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e40af' }}>
            {selected.length} article{selected.length > 1 ? 's' : ''} sélectionné{selected.length > 1 ? 's' : ''}
          </span>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>→ Déplacer vers :</span>
          <select value={moveTarget} onChange={e => setMoveTarget(e.target.value)}
            style={{ padding: '5px 10px', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '12px' }}>
            <option value="">-- Choisir catégorie --</option>
            {categoriesPresentes.filter(c => c !== 'Tous').map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={handleMoveSelected} disabled={!moveTarget}
            style={{ padding: '5px 14px', background: moveTarget ? '#1e40af' : '#e5e7eb',
              color: moveTarget ? 'white' : '#9ca3af', border: 'none', borderRadius: '6px',
              fontSize: '12px', fontWeight: 600, cursor: moveTarget ? 'pointer' : 'default' }}>
            Déplacer
          </button>
          <button onClick={() => setSelected([])}
            style={{ padding: '5px 10px', background: '#fee2e2', color: '#dc2626', border: 'none',
              borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
            Annuler
          </button>
        </div>
      )}

      {/* Compteur résultats */}
      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px' }}>
        {produitsFiltres.length} produit(s)
        {search && <span> pour "{search}"</span>}
      </div>

      {/* Tableau */}
      <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: '#1e40af', color: 'white' }}>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, width: '32px' }}>
                <input type="checkbox" onChange={e => setSelected(e.target.checked ? produitsFiltres.map(p => p.id) : [])}
                  checked={selected.length === produitsFiltres.length && produitsFiltres.length > 0} />
              </th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, width: '60px' }}>Photo</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Catégorie</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Réf / Nom</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Prix FCFA</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Accessoires</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Installation</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>Vedette</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>Actif</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {produitsFiltres.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: '#9ca3af' }}>
                  Aucun produit trouv\u00e9
                </td>
              </tr>
            ) : produitsFiltres.map((p, idx) => {
              const isEditing = editingId === p.id;
              const isSaved = savedId === p.id;
              return (
                <tr key={p.id} style={{ background: idx % 2 === 0 ? '#fafafa' : 'white',
                  borderBottom: '1px solid #f3f4f6', opacity: p.actif ? 1 : 0.5 }}>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <input type="checkbox" checked={selected.includes(p.id)}
                      onChange={() => toggleSelect(p.id)} />
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      {p.image_url ? (
                        <div style={{ position: 'relative' }}>
                          <img src={p.image_url} alt={p.nom}
                            style={{ width: '44px', height: '44px', objectFit: 'contain',
                              borderRadius: '6px', border: '1px solid #e5e7eb', background: '#f8fafc' }} />
                          <button onClick={() => handlePhotoDelete(p)}
                            title="Supprimer la photo"
                            style={{ position: 'absolute', top: '-6px', right: '-6px', width: '16px', height: '16px',
                              borderRadius: '50%', background: '#ef4444', color: 'white', border: 'none',
                              cursor: 'pointer', fontSize: '9px', display: 'flex', alignItems: 'center',
                              justifyContent: 'center', lineHeight: 1 }}>✕</button>
                        </div>
                      ) : (
                        <div style={{ width: '44px', height: '44px', borderRadius: '6px',
                          border: '2px dashed #d1d5db', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', background: '#f9fafb', fontSize: '18px' }}>
                          📷
                        </div>
                      )}
                      <label style={{ position: 'absolute', inset: 0, cursor: 'pointer', opacity: 0 }}>
                        <input type="file" accept="image/*" style={{ display: 'none' }}
                          onChange={e => { if (e.target.files[0]) handlePhotoUpload(p, e.target.files[0]); e.target.value = ''; }} />
                      </label>
                      {uploadingPhoto === p.id && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.8)',
                          borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '10px', color: '#6b7280' }}>⏳</div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {editCategorie[p.id] ? (
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <select value={editCategorie[p.id] === '__editing__' ? p.categorie : editCategorie[p.id]}
                          onChange={e => setEditCategorie({...editCategorie, [p.id]: e.target.value})}
                          style={{ padding: '3px 6px', border: '1px solid #bfdbfe', borderRadius: '4px', fontSize: '11px' }}>
                          {categoriesPresentes.filter(c => c !== 'Tous').map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button onClick={() => handleCategorieProduit(p, editCategorie[p.id] === '__editing__' ? p.categorie : editCategorie[p.id])}
                          style={{ padding: '2px 8px', background: '#1e40af', color: 'white', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>OK</button>
                        <button onClick={() => setEditCategorie({})}
                          style={{ padding: '2px 6px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>✕</button>
                      </div>
                    ) : (
                      <span onClick={() => setEditCategorie({[p.id]: '__editing__'})}
                        title="Cliquer pour changer de catégorie"
                        style={{ background: '#eff6ff', color: '#1e40af', fontSize: '10px',
                          fontWeight: 600, padding: '2px 8px', borderRadius: '20px', cursor: 'pointer' }}>
                        {p.categorie} ✏️
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {isEditing ? (
                      <input value={editValues.nom} onChange={e => setEditValues({...editValues, nom: e.target.value})}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '4px 8px', border: '1px solid #bfdbfe',
                          borderRadius: '4px', fontSize: '12px' }} />
                    ) : (
                      <>
                        <div style={{ fontWeight: 600, color: '#1e40af', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          {p.ref}
                          {produitIncomplet(p) && (
                            <span
                              title={"Champs manquants : " + validerProduit(p).manquants.map(m => LIBELLES_CHAMPS[m] || m).join(', ')}
                              style={{ background: '#fef3c7', color: '#92400e', fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', cursor: 'help' }}>
                              ⚠ Incomplet
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '11px' }}>{p.nom}</div>
                      </>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    {isEditing ? (
                      <input type="number" value={editValues.prix}
                        onChange={e => setEditValues({...editValues, prix: e.target.value})}
                        style={{ width: '100px', padding: '4px 8px', border: '1px solid #bfdbfe',
                          borderRadius: '4px', fontSize: '12px', textAlign: 'right' }} />
                    ) : (
                      <span style={{ fontWeight: 600, color: '#374151' }}>{fmt(p.prix)}</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    {isEditing ? (
                      <input type="number" value={editValues.prix_accessoires}
                        onChange={e => setEditValues({...editValues, prix_accessoires: e.target.value})}
                        style={{ width: '90px', padding: '4px 8px', border: '1px solid #bfdbfe',
                          borderRadius: '4px', fontSize: '12px', textAlign: 'right' }} />
                    ) : (
                      <span style={{ color: '#6b7280' }}>{p.prix_accessoires ? fmt(p.prix_accessoires) : '—'}</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    {isEditing ? (
                      <input type="number" value={editValues.prix_installation}
                        onChange={e => setEditValues({...editValues, prix_installation: e.target.value})}
                        style={{ width: '90px', padding: '4px 8px', border: '1px solid #bfdbfe',
                          borderRadius: '4px', fontSize: '12px', textAlign: 'right' }} />
                    ) : (
                      <span style={{ color: '#6b7280' }}>{p.prix_installation ? fmt(p.prix_installation) : '—'}</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <button onClick={() => toggleVedette(p)}
                      title={p.vedette ? 'Retirer vedette' : 'Marquer vedette'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                      <Star size={16} fill={p.vedette ? '#f59e0b' : 'none'}
                        color={p.vedette ? '#f59e0b' : '#d1d5db'} />
                    </button>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <button onClick={() => toggleActif(p)}
                      title={p.actif ? 'D\u00e9sactiver' : 'Activer'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                      {p.actif
                        ? <Eye size={16} color="#10b981" />
                        : <EyeOff size={16} color="#d1d5db" />}
                    </button>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                      {isEditing ? (
                        <>
                          <button onClick={() => saveEdit(p.id)} disabled={saving}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px',
                              padding: '4px 10px', background: isSaved ? '#10b981' : '#1e40af',
                              color: 'white', border: 'none', borderRadius: '4px',
                              fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                            <Save size={12} /> {isSaved ? 'OK' : 'Sauv.'}
                          </button>
                          <button onClick={() => setEditingId(null)}
                            style={{ padding: '4px 8px', background: '#f3f4f6', color: '#374151',
                              border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>
                            Annuler
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startFullEdit(p)}
                            title="Edition complete (champs techniques)"
                            style={{ background: 'none', border: '1px solid #d1d5db', cursor: 'pointer', padding: '4px 6px', borderRadius: '4px' }}>
                            <Settings size={14} color="#6b7280" />
                          </button>
                          <button onClick={() => startEdit(p)}
                            style={{ padding: '4px 10px', background: '#eff6ff', color: '#1e40af',
                              border: '1px solid #bfdbfe', borderRadius: '4px',
                              fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                            Modifier
                          </button>
                          <button onClick={() => deleteProduit(p.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                            <Trash2 size={14} color="#ef4444" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>

      {fullEditId !== null && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setFullEditId(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'white', borderRadius: '12px',
            padding: '24px', maxWidth: '720px', width: '90%',
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e40af' }}>
                Edition complete du produit
              </h2>
              <button onClick={() => setFullEditId(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                <X size={22} color="#6b7280" />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px', fontWeight: 600 }}>Reference *</label>
                <input value={fullEditValues.ref || ''}
                  onChange={e => setFullEditValues({...fullEditValues, ref: e.target.value})}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px', fontWeight: 600 }}>Nom *</label>
                <input value={fullEditValues.nom || ''}
                  onChange={e => setFullEditValues({...fullEditValues, nom: e.target.value})}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px', fontWeight: 600 }}>Categorie</label>
                <input value={fullEditValues.categorie || ''} disabled
                  style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px', background: '#f9fafb', color: '#6b7280' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px', fontWeight: 600 }}>Prix (FCFA) *</label>
                <input type="number" value={fullEditValues.prix ?? ''}
                  onChange={e => setFullEditValues({...fullEditValues, prix: e.target.value})}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '3px', fontWeight: 600 }}>Ordre</label>
                <input type="number" value={fullEditValues.ordre ?? 0}
                  onChange={e => setFullEditValues({...fullEditValues, ordre: parseInt(e.target.value) || 0})}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!fullEditValues.vedette}
                    onChange={e => setFullEditValues({...fullEditValues, vedette: e.target.checked})} />
                  Vedette
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={fullEditValues.actif !== false}
                    onChange={e => setFullEditValues({...fullEditValues, actif: e.target.checked})} />
                  Actif
                </label>
              </div>
            </div>

            {getChampsTechniques(fullEditValues.categorie).length > 0 && (
              <div style={{ padding: '14px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', marginBottom: '16px' }}>
                <div style={{ fontWeight: 700, fontSize: '12px', color: '#92400e', marginBottom: '12px' }}>
                  Caracteristiques techniques requises pour {fullEditValues.categorie}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                  {getChampsTechniques(fullEditValues.categorie).map(champ => (
                    <div key={champ}>
                      <label style={{ fontSize: '11px', color: '#92400e', display: 'block', marginBottom: '3px', fontWeight: 600 }}>
                        {LIBELLES_CHAMPS[champ] || champ} <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <input
                        type={TYPE_CHAMPS[champ] || 'text'}
                        step="0.01"
                        value={fullEditValues[champ] ?? ''}
                        onChange={e => setFullEditValues({...fullEditValues, [champ]: e.target.value})}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '12px' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setFullEditId(null)}
                style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={saveFullEdit}
                disabled={!validerProduit(fullEditValues).valide}
                title={!validerProduit(fullEditValues).valide ? 'Champs requis manquants : ' + validerProduit(fullEditValues).manquants.map(m => LIBELLES_CHAMPS[m] || m).join(', ') : 'Enregistrer les modifications'}
                style={{
                  padding: '8px 20px',
                  background: validerProduit(fullEditValues).valide ? '#1e40af' : '#9ca3af',
                  color: 'white', border: 'none', borderRadius: '6px',
                  fontSize: '12px', fontWeight: 600,
                  cursor: validerProduit(fullEditValues).valide ? 'pointer' : 'not-allowed',
                  opacity: validerProduit(fullEditValues).valide ? 1 : 0.6,
                }}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
        </table>
      </div>
    </div>
  );
};

export default CatalogueManager;
